import { create } from 'zustand';
import { setAuthToken, setOnUnauthenticated } from './apiClient';
import type { CareTeamProfileDto } from '../api/auth';

// Web session store. Token is kept in localStorage (30-day provider JWT issued by
// /care-team-auth/login). Phase 3+ can harden this to an httpOnly cookie + refresh
// route without touching callers.

const TOKEN_KEY = 'fasd.provider.token';
const PROFILE_KEY = 'fasd.provider.profile';

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function readProfile(): CareTeamProfileDto | null {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw ? (JSON.parse(raw) as CareTeamProfileDto) : null;
  } catch {
    return null;
  }
}

type SessionState = {
  token: string | null;
  profile: CareTeamProfileDto | null;
  hydrated: boolean;
  signIn: (token: string, profile: CareTeamProfileDto) => void;
  setProfile: (profile: CareTeamProfileDto) => void;
  signOut: () => void;
};

export const useSession = create<SessionState>((set) => ({
  token: null,
  profile: null,
  hydrated: false,

  signIn: (token, profile) => {
    try {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    } catch {
      /* private mode / storage disabled — session stays in memory only */
    }
    setAuthToken(token);
    set({ token, profile, hydrated: true });
  },

  setProfile: (profile) => {
    try {
      localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    } catch {
      /* ignore */
    }
    set({ profile });
  },

  signOut: () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(PROFILE_KEY);
    } catch {
      /* ignore */
    }
    setAuthToken(null);
    set({ token: null, profile: null, hydrated: true });
  },
}));

/** Called once at app startup, before the router renders. */
export function bootstrapSession() {
  const token = readToken();
  const profile = readProfile();
  if (token) setAuthToken(token);
  useSession.setState({ token, profile, hydrated: true });

  // On any 401 from the API, drop the session so the router bounces to /login.
  setOnUnauthenticated(() => {
    useSession.getState().signOut();
  });
}
